import { Component, createRef, type ErrorInfo, type ReactNode } from "react";
import { captureBoundaryError } from "@/lib/sentry";
import { getBusinessConfig } from "@/lib/site-config";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  referenceId: string | null;
  refCopied: boolean;
}


/**
 * Generates an 8-character uppercase hex reference ID for a boundary
 * incident. Matches the booking reference format so support can trace
 * incidents consistently. Uses `crypto.randomUUID` when available and
 * falls back to `Math.random` in ancient runtimes.
 */
function generateReferenceId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase();
    }
  } catch {}
  return Math.random().toString(16).slice(2, 10).toUpperCase().padEnd(8, "0");
}

// Reference id survives a Reload click so support quotes stay stable across
// the customer's retry. Scoped to the current path + a short freshness
// window so unrelated later crashes get a fresh id.
const REF_STORAGE_KEY = "woodseer:last-error-ref";
const REF_TTL_MS = 5 * 60 * 1000;

function readStickyReferenceId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(REF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; path?: string; ts?: number };
    if (!parsed?.id || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > REF_TTL_MS) return null;
    if (parsed.path && parsed.path !== window.location.pathname) return null;
    return parsed.id;
  } catch {
    return null;
  }
}

function writeStickyReferenceId(id: string) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      REF_STORAGE_KEY,
      JSON.stringify({ id, path: window.location.pathname, ts: Date.now() }),
    );
  } catch {}
}

function clearStickyReferenceId() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(REF_STORAGE_KEY);
  } catch {}
}

/**
 * Global error boundary. Catches render-time errors anywhere in the tree
 * and renders a safe, on-brand fallback with an incident reference ID so
 * users can quote it to support and we can trace the event in logs / Sentry.
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, referenceId: null, refCopied: false };

  static getDerivedStateFromError(error: Error): State {
    const sticky = readStickyReferenceId();
    const referenceId = sticky ?? generateReferenceId();
    if (!sticky) writeStickyReferenceId(referenceId);
    return { hasError: true, error, referenceId, refCopied: false };

  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Best-effort logging; never throw from the boundary itself.
    try {
      const referenceId = this.state.referenceId ?? generateReferenceId();
      if (!this.state.referenceId) this.setState({ referenceId });
      writeStickyReferenceId(referenceId);
      // eslint-disable-next-line no-console
      console.error("[GlobalErrorBoundary]", referenceId, error, info.componentStack);
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        const search = window.location.search;
        try {
          captureBoundaryError(
            error,
            { componentStack: info.componentStack, referenceId },
            { path, search },
          );
        } catch {}
        void fetch("/api/public/frontend-error-log", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reference_id: referenceId,
            path,
            search,
            kind: "render_error",
            error: error.message,
            error_stack: error.stack ?? null,
            component_stack: info.componentStack ?? null,
            referer: document.referrer || null,
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch {}
  }

  handleReset = () => {
    clearStickyReferenceId();
    this.setState({ hasError: false, error: null, referenceId: null, refCopied: false });

    if (typeof window !== "undefined") {
      window.location.assign("/");
    }
  };

  handleReload = () => {
    // Keep the sticky ref intact so the next render shows the same id.
    if (typeof window !== "undefined") window.location.reload();
  };

  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  handleCopyReference = async () => {
    const ref = this.state.referenceId;
    if (!ref) return;
    // Prefer the async Clipboard API; fall back to a hidden textarea +
    // execCommand for older browsers or non-secure-context previews.
    let ok = false;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(ref);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok && typeof document !== "undefined") {
      try {
        const ta = document.createElement("textarea");
        ta.value = ref;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      this.setState({ refCopied: true });
      if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => {
        this.setState({ refCopied: false });
      }, 2000);
    }
  };

  componentWillUnmount() {
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
  }



  private primaryActionRef = createRef<HTMLButtonElement>();

  componentDidUpdate(_prev: Props, prevState: State) {
    // On the transition into the error state, move focus to the primary
    // recovery action so keyboard/AT users land on the most useful control
    // instead of being stranded on whatever had focus when the crash fired.
    if (!prevState.hasError && this.state.hasError) {
      // Defer to next tick so the fallback DOM is mounted before we focus.
      queueMicrotask(() => {
        this.primaryActionRef.current?.focus();
      });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const ref = this.state.referenceId ?? "";
    const business = getBusinessConfig();
    const supportEmail = business.email ?? "hello@woodseerautos.co.uk";
    const supportTel = business.phone ?? "";
    const subject = encodeURIComponent(`Website error — ref ${ref}`);
    const body = encodeURIComponent(
      `Hi Woodseer team,\n\nI hit an error on the website.\n\n` +
        `Reference: ${ref}\n` +
        `Page: ${typeof window !== "undefined" ? window.location.href : ""}\n` +
        `Time: ${new Date().toISOString()}\n\n` +
        `What I was doing:\n`,
    );
    const mailto = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
    const tel = supportTel ? `tel:${supportTel.replace(/\s+/g, "")}` : null;

    return (
      <div
        className="flex min-h-[70vh] items-center justify-center bg-background px-4 py-20"
        role="alert"
        aria-labelledby="global-error-heading"
        aria-describedby="global-error-description"
        data-testid="global-error-fallback"
      >
        <div className="max-w-xl text-center">
          <p className="text-sm font-semibold text-primary uppercase tracking-widest">
            Something went wrong
          </p>
          <h1
            id="global-error-heading"
            className="mt-3 text-4xl md:text-5xl font-bold text-foreground"
            tabIndex={-1}
          >
            We hit a snag loading this page
          </h1>
          <p
            id="global-error-description"
            className="mt-4 text-muted-foreground leading-relaxed"
          >
            Sorry about that — the page couldn't render properly. Try reloading,
            or head back to the homepage. If it keeps happening, quote the
            reference below and we'll trace it straight away.
          </p>

          {ref ? (
            <div
              className="mt-6 inline-flex flex-col items-center gap-1 rounded-md border border-border bg-muted/60 px-4 py-3"
              role="status"
              aria-live="polite"
              data-testid="error-reference"
            >
              <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Error reference
              </span>
              <span
                className="font-mono text-lg font-bold text-foreground tracking-wider"
                data-testid="error-reference-value"
              >
                {ref}
              </span>
              <button
                type="button"
                onClick={this.handleCopyReference}
                data-testid="error-copy-reference"
                aria-label={`Copy reference ID ${ref}`}
                className="mt-2 inline-flex items-center justify-center rounded border border-border bg-background px-3 py-1 text-xs font-semibold text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {this.state.refCopied ? "Copied" : "Copy reference ID"}
              </button>
              <span data-testid="error-copy-status" className="sr-only" aria-live="polite">
                {this.state.refCopied ? `Reference ID ${ref} copied to clipboard` : ""}
              </span>
            </div>

          ) : null}

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              ref={this.primaryActionRef}
              type="button"
              onClick={this.handleReload}
              data-testid="error-primary-action"
              className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="inline-flex items-center justify-center rounded-md border border-border bg-secondary px-6 py-3 text-sm font-semibold text-secondary-foreground hover:bg-secondary/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Back to homepage
            </button>
          </div>


          <p className="mt-6 text-sm text-muted-foreground">
            Need a hand?{" "}
            <a
              href={mailto}
              className="font-semibold text-primary underline-offset-4 hover:underline"
              data-testid="error-contact-email"
            >
              Email support
            </a>
            {tel ? (
              <>
                {" "}or{" "}
                <a
                  href={tel}
                  className="font-semibold text-primary underline-offset-4 hover:underline"
                  data-testid="error-contact-phone"
                >
                  call {supportTel}
                </a>
              </>
            ) : null}
            {ref ? <> — quote reference <span className="font-mono font-semibold">{ref}</span>.</> : "."}
          </p>

          {import.meta.env.DEV && this.state.error ? (
            <pre className="mt-8 max-h-64 overflow-auto rounded-md border border-border bg-muted p-4 text-left text-xs text-muted-foreground">
              {this.state.error.stack || this.state.error.message}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}
